import { Badge, Button, Progress } from "@nfp-tracker/components";
import { CheckCircle2, Sparkles } from "lucide-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChecklistRow } from "../components/getting-started/ChecklistRow";
import { ReleaseReviewStep } from "../components/getting-started/ReleaseReviewStep";
import { useGettingStartedWhatsNew } from "../hooks/useCurrentUserWhatsNew";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import type { OnboardingStepView } from "../hooks/useOnboardingChecklist";
import { useOnboardingChecklist } from "../hooks/useOnboardingChecklist";
import { trackEvent } from "../integrations/analytics/client";

/**
 * Getting Started: a compact header, then one card per checklist step.
 *
 * Steps are the onboarding checklist (constants/onboardingChecklist.ts) plus the permanent
 * "Review the latest release" step (constants/whatsNew.ts). useOnboardingChecklist folds
 * that release step into totalCount / completedCount / allComplete, so a new release resurfaces
 * Get Started (nav entry, home card) until the release dialog has been opened once.
 */
const GettingStarted = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktopViewport();
  const whatsNew = useGettingStartedWhatsNew();
  const {
    loading,
    steps,
    releaseStep,
    completedCount,
    totalCount,
    percentComplete,
    allComplete,
    completeStep,
    dismissStep,
  } = useOnboardingChecklist();

  const viewedRef = useRef(false);
  useEffect(() => {
    if (loading || viewedRef.current) return;
    viewedRef.current = true;
    trackEvent("getting_started_viewed", { completedCount, totalCount });
  }, [loading, completedCount, totalCount]);

  const handleCta = (view: OnboardingStepView) => {
    if (view.step.requiresDesktop && !isDesktop) return;
    trackEvent("onboarding_step_cta_clicked", { stepId: view.step.id });
    const { action } = view.step;
    if (action.kind === "external") {
      window.open(action.href, "_blank", "noopener,noreferrer");
    } else {
      navigate(action.route);
    }
    if (view.step.completeOnCta) {
      void completeStep(view.step.id, "manual");
    }
  };

  // An uncompleted release is the only thing left for finished users, so it leads; otherwise
  // it sits last (and stays listed, muted, once done).
  const releaseLeads =
    Boolean(releaseStep && !releaseStep.completed) &&
    steps.every((s) => s.completed || s.dismissed);

  const orderRows = (stepRows: JSX.Element[]) => {
    if (!releaseStep) return stepRows;
    const releaseRow = (
      <ReleaseReviewStep key="latest-release" whatsNew={whatsNew} />
    );
    return releaseLeads ? [releaseRow, ...stepRows] : [...stepRows, releaseRow];
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4">
      <div className="space-y-2 px-1">
        <div className="flex items-center gap-2">
          {allComplete ? (
            <CheckCircle2 className="h-5 w-5 text-primary" />
          ) : (
            <Sparkles className="h-5 w-5 text-primary" />
          )}
          <h2 className="text-lg font-semibold">
            {allComplete ? "You're all set" : "Let's get you set up"}
          </h2>
        </div>
        <p className="text-sm text-muted-foreground">
          {allComplete
            ? "You've finished the recommended setup. New features show up here when they land."
            : "A few quick steps to get the most out of NFP Tracker."}
        </p>
        <div className="flex items-center gap-3 pt-1">
          <Progress value={percentComplete} className="h-2 flex-1" />
          <span className="shrink-0 text-xs font-medium text-muted-foreground">
            {completedCount} of {totalCount} done
          </span>
        </div>
      </div>

      <div className="space-y-3">
        {/* One keyed array, so moving the release row (e.g. when opening its dialog completes
            it) reorders it instead of remounting it and dropping the open dialog. */}
        {orderRows(
          steps.map((view) => {
            const isDone = view.completed;
            const isSkipped = view.dismissed && !isDone;
            const desktopBlocked = Boolean(
              view.step.requiresDesktop && !isDesktop,
            );

            return (
              <ChecklistRow
                key={view.step.id}
                icon={view.step.icon}
                title={view.step.title}
                description={view.step.description}
                done={isDone}
                muted={isDone || isSkipped}
                badges={
                  <>
                    {isDone && (
                      <Badge variant="secondary" className="text-[11px]">
                        Done
                      </Badge>
                    )}
                    {isSkipped && (
                      <Badge variant="outline" className="text-[11px]">
                        Skipped
                      </Badge>
                    )}
                  </>
                }
                actions={
                  <>
                    <Button
                      size="sm"
                      variant={isDone || isSkipped ? "outline" : "default"}
                      disabled={desktopBlocked}
                      onClick={() => handleCta(view)}
                    >
                      {view.step.ctaLabel}
                    </Button>
                    {desktopBlocked && (
                      <span className="text-xs text-muted-foreground">
                        Switch to a larger screen to use this
                      </span>
                    )}
                    {!isDone && !isSkipped && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            void completeStep(view.step.id, "manual")
                          }
                        >
                          Mark as done
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-muted-foreground"
                          onClick={() => void dismissStep(view.step.id)}
                        >
                          Skip
                        </Button>
                      </>
                    )}
                  </>
                }
              />
            );
          }),
        )}
      </div>
    </div>
  );
};

export default GettingStarted;
